/* ══════════════════════════════════════════════════════════════════════
   IAM POLICY CATEGORIZATION — triage board.

   Four severity buckets; drag a policy card between them to recategorize
   it. "Uncategorized" leads because it is the backlog: those are the
   policies nobody has risk-rated yet, and clearing that column is the
   job this page exists to do.

   Mock / local state only. A drop updates state so the interaction is
   complete end to end, but nothing is persisted — see onDragEnd for the
   single place a save call would go.

   Built on @dnd-kit/core, matching ConfigurableCardGrid and
   KeyMetricsConfigModal rather than introducing another DnD library.
   ══════════════════════════════════════════════════════════════════════ */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  pointerWithin,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  AlertCircle,
  GripVertical,
  Loader2,
  Pencil,
  RefreshCw,
  Save,
  Search,
  ShieldAlert,
  X,
} from "lucide-react";
import { PageShell } from "../../components/layout/PageShell";
import { EmptyState } from "../../components/EmptyState";
import { useToastStore } from "../../../stores/toastStore";
import {
  fetchCategorizedPolicyList,
  savePolicyCategorization,
  type PagCategorizedPolicy,
} from "../../../api/frameworksApi";
import {
  PAG_SEVERITY_META,
  PAG_SEVERITY_ORDER,
  type PagSeverity,
} from "./data/pagTypes";

type IamPolicy = PagCategorizedPolicy;

/* ── Board model ────────────────────────────────────────────────────── */

type PolicyCard = IamPolicy;
type Board = Record<PagSeverity, PolicyCard[]>;

/* Uncategorized first: it is the queue being worked down, not an
   afterthought. The rest follow the shared severity order. */
const COLUMN_ORDER: PagSeverity[] = [
  "Uncategorized",
  ...PAG_SEVERITY_ORDER.filter((s) => s !== "Uncategorized"),
];

const emptyBoard = (): Board => ({
  High: [],
  Medium: [],
  Low: [],
  Uncategorized: [],
});

const byName = (a: PolicyCard, b: PolicyCard) =>
  a.policyName.localeCompare(b.policyName);

/** Case-insensitive match of a response key onto a known bucket, so an
 *  unexpected casing ("LOW") still lands somewhere sensible. */
const toSeverity = (key: string): PagSeverity | null =>
  COLUMN_ORDER.find((s) => s.toLowerCase() === key.trim().toLowerCase()) ?? null;

/** API response -> board. Anything in an unrecognised bucket falls into
 *  Uncategorized rather than being silently dropped. */
function toBoard(res: Record<string, IamPolicy[]> | null | undefined): Board {
  const board = emptyBoard();
  Object.entries(res ?? {}).forEach(([key, list]) => {
    const bucket = toSeverity(key) ?? "Uncategorized";
    board[bucket].push(...(list ?? []));
  });
  COLUMN_ORDER.forEach((s) => board[s].sort(byName));
  return board;
}

/* ── Card ───────────────────────────────────────────────────────────── */

function PolicyCardBody({
  card,
  severity,
  dragging = false,
}: {
  card: PolicyCard;
  severity: PagSeverity;
  dragging?: boolean;
}) {
  const color = PAG_SEVERITY_META[severity].color;
  const inline = card.policyArn === "Inline";

  return (
    <div
      className={`group flex items-start gap-2 rounded-lg border bg-card p-2.5 transition-shadow ${
        dragging ? "shadow-lg" : "shadow-sm hover:shadow-md"
      }`}
      style={{ borderColor: `${color}59`, borderLeft: `3px solid ${color}` }}
    >
      <GripVertical className="mt-0.5 size-3.5 shrink-0 text-muted-foreground/50" />
      <div className="min-w-0 flex-1">
        <p
          className="truncate text-[13px] font-semibold leading-5 text-[#1e293b] dark:text-white"
          title={card.policyName}
        >
          {card.policyName}
        </p>
        <p
          className="truncate text-[11px] leading-4 text-[#94A3B8] dark:text-white/40"
          title={card.policyArn}
        >
          {inline ? "Inline policy" : card.policyArn}
        </p>
        {card.softDelete && (
          <span className="mt-1 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold text-[#B45309] dark:text-amber-300"
            style={{ background: "#F59E0C1f" }}
          >
            Soft-deleted
          </span>
        )}
      </div>
    </div>
  );
}

function DraggableCard({
  card,
  severity,
  editing,
}: {
  card: PolicyCard;
  severity: PagSeverity;
  editing: boolean;
}) {
  /* dnd-kit's own `disabled` rather than skipping the hook: hook order has
     to stay stable across renders when edit mode toggles. */
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: String(card.id),
    data: { from: severity },
    disabled: !editing,
  });

  return (
    <div
      ref={setNodeRef}
      {...(editing ? listeners : {})}
      {...attributes}
      /* The card stays in place at reduced opacity while a DragOverlay copy
         follows the cursor — moving the original would reflow the column
         under the pointer. */
      className={`touch-none outline-none ${
        editing ? "cursor-grab active:cursor-grabbing" : "cursor-default"
      } ${isDragging ? "opacity-30" : ""}`}
    >
      <PolicyCardBody card={card} severity={severity} />
    </div>
  );
}

/* ── Column ─────────────────────────────────────────────────────────── */

function Column({
  severity,
  cards,
  activeFrom,
  editing,
}: {
  severity: PagSeverity;
  cards: PolicyCard[];
  activeFrom: PagSeverity | null;
  editing: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: severity });
  const meta = PAG_SEVERITY_META[severity];

  /* Only light up columns that would actually change something — the
     column a card came from is not a useful drop target. */
  const isCandidate = activeFrom !== null && activeFrom !== severity;

  return (
    <div className="flex min-w-0 flex-col rounded-xl border border-border bg-card">
      <div
        className="flex items-center gap-2 rounded-t-xl border-b border-border px-3 py-2.5"
        style={{ background: `${meta.color}14` }}
      >
        <span
          className="size-2.5 shrink-0 rounded-full"
          style={{ background: meta.color }}
        />
        <h3 className="text-[13px] font-semibold text-[#1e293b] dark:text-white">
          {meta.label}
        </h3>
        <span
          className="ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums"
          style={{ background: `${meta.color}1f`, color: meta.color }}
        >
          {cards.length}
        </span>
      </div>

      {/* Fixed-height scroll area: Uncategorized holds ~120 cards, and
          letting a column grow to fit them would make the page metres tall
          and put the other columns out of reach. */}
      <div
        ref={setNodeRef}
        className={`flex h-[calc(100vh-320px)] min-h-[260px] flex-col gap-2 overflow-y-auto p-2.5 transition-colors ${
          isOver && isCandidate
            ? "bg-indigo-500/10"
            : isCandidate
              ? "bg-muted/40"
              : ""
        }`}
      >
        {cards.length === 0 ? (
          <p className="m-auto px-2 text-center text-[11px] text-muted-foreground">
            {isCandidate ? "Drop here" : "No policies"}
          </p>
        ) : (
          cards.map((c) => (
            <DraggableCard key={c.id} card={c} severity={severity} editing={editing} />
          ))
        )}
      </div>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────── */

export function IamPolicyCategorization() {
  const showSuccess = useToastStore((s) => s.showSuccess);
  const showError = useToastStore((s) => s.showError);
  const [board, setBoard] = useState<Board>(emptyBoard);
  /* The board as last loaded/saved. Cancel restores it, and Save diffs
     against it to send only what actually moved. */
  const [baseline, setBaseline] = useState<Board>(emptyBoard);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeFrom, setActiveFrom] = useState<PagSeverity | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchCategorizedPolicyList()
      .then((res) => {
        const next = toBoard(res);
        setBoard(next);
        setBaseline(next);
        setEditing(false);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Could not load policies"),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const sensors = useSensors(
    /* A small distance threshold so a click on a card is not swallowed as
       a drag — same activation constraint ConfigurableCardGrid uses. */
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  /* Search filters what is shown, never what is stored: a drop always
     moves the real card, so filtering cannot lose data. */
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return board;
    const next = emptyBoard();
    COLUMN_ORDER.forEach((s) => {
      next[s] = board[s].filter(
        (c) =>
          c.policyName.toLowerCase().includes(q) ||
          c.policyArn.toLowerCase().includes(q),
      );
    });
    return next;
  }, [board, search]);

  const totals = useMemo(() => {
    const all = COLUMN_ORDER.reduce((n, s) => n + board[s].length, 0);
    return { all, untriaged: board.Uncategorized.length };
  }, [board]);

  const activeCard = useMemo(() => {
    if (!activeId || !activeFrom) return null;
    return board[activeFrom].find((c) => String(c.id) === activeId) ?? null;
  }, [activeId, activeFrom, board]);

  const onDragStart = (e: DragStartEvent) => {
    setActiveId(String(e.active.id));
    setActiveFrom((e.active.data.current?.from as PagSeverity) ?? null);
  };

  const onDragEnd = (e: DragEndEvent) => {
    const from = (e.active.data.current?.from as PagSeverity) ?? null;
    const to = e.over?.id as PagSeverity | undefined;
    setActiveId(null);
    setActiveFrom(null);
    if (!from || !to || from === to) return;

    setBoard((prev) => {
      const card = prev[from].find((c) => String(c.id) === String(e.active.id));
      if (!card) return prev;
      return {
        ...prev,
        [from]: prev[from].filter((c) => c.id !== card.id),
        [to]: [...prev[to], card].sort((a, b) =>
          a.policyName.localeCompare(b.policyName),
        ),
      };
    });
  };

  /* Only policies whose bucket differs from the baseline, grouped by their
     NEW severity. The save endpoint wants lowercase ("low", not "Low"). */
  const changes = useMemo(() => {
    const wasIn = new Map<number, PagSeverity>();
    COLUMN_ORDER.forEach((s) => baseline[s].forEach((c) => wasIn.set(c.id, s)));

    return COLUMN_ORDER.map((s) => ({
      severity: s.toLowerCase(),
      ids: board[s].filter((c) => wasIn.get(c.id) !== s).map((c) => c.id),
    })).filter((g) => g.ids.length > 0);
  }, [board, baseline]);

  const movedCount = changes.reduce((n, g) => n + g.ids.length, 0);

  const cancel = () => {
    setBoard(baseline);
    setEditing(false);
    setSearch("");
  };

  const save = () => {
    if (!movedCount) {
      setEditing(false);
      return;
    }
    setSaving(true);
    savePolicyCategorization(changes)
      .then(() => {
        showSuccess(
          "Categorization saved",
          `${movedCount} ${movedCount === 1 ? "policy" : "policies"} recategorized.`,
        );
        setEditing(false);
        // Re-read rather than trusting local state: the server owns
        // updatedOn and may normalize anything else.
        load();
      })
      .catch((e: unknown) =>
        showError(
          "Could not save",
          e instanceof Error ? e.message : "The categorization was not saved.",
        ),
      )
      .finally(() => setSaving(false));
  };

  return (
    <PageShell className="space-y-4">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            IAM Policy Categorization
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {editing
              ? movedCount
                ? `${movedCount} ${movedCount === 1 ? "policy" : "policies"} moved. Save to apply.`
                : "Drag a policy into a risk band to categorize it."
              : `${totals.untriaged} of ${totals.all} policies are still uncategorized.`}
          </p>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <div className="relative w-full sm:w-[240px]">
            <input
              type="text"
              value={search}
              maxLength={120}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search policies"
              className="h-9 w-full rounded-lg border border-border bg-background pl-3 pr-9 text-sm text-foreground outline-none transition-colors focus:border-indigo-500"
            />
            {search ? (
              <button
                onClick={() => setSearch("")}
                title="Clear search"
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            ) : (
              <Search className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            )}
          </div>

          {/* Read-only until Edit is pressed, so a stray drag cannot
              recategorize a policy by accident. */}
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              disabled={loading || !!error || totals.all === 0}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-indigo-500 disabled:cursor-default disabled:opacity-40"
            >
              <Pencil className="size-3.5" />
              Edit
            </button>
          ) : (
            <>
              <button
                onClick={cancel}
                disabled={saving}
                className="inline-flex h-9 items-center rounded-lg border border-border px-4 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving || !movedCount}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-indigo-500 disabled:cursor-default disabled:opacity-40"
              >
                {saving ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Save className="size-3.5" />
                )}
                {saving ? "Saving" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Board ──────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-border bg-card py-20 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading policies
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card py-16 text-center">
          <div className="mb-3 flex size-11 items-center justify-center rounded-full bg-red-500/10">
            <AlertCircle className="size-5 text-red-600 dark:text-red-400" />
          </div>
          <p className="text-[13px] font-semibold text-red-700 dark:text-red-300">
            Something went wrong.
          </p>
          <p className="mt-1 max-w-md text-[13px] text-muted-foreground">{error}</p>
          <button
            onClick={load}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-background px-3 py-1.5 text-[12px] font-semibold text-red-700 transition-colors hover:bg-red-50 dark:border-red-500/30 dark:text-red-300 dark:hover:bg-red-500/10"
          >
            <RefreshCw className="size-3.5" />
            Retry
          </button>
        </div>
      ) : totals.all === 0 ? (
        <EmptyState
          icon={ShieldAlert}
          title="No policies to categorize"
          description="No IAM policies were found for the current applications."
        />
      ) : (
        <DndContext
          sensors={sensors}
          /* pointerWithin rather than closestCenter: columns are large drop
             zones, and "whichever column the cursor is inside" is the
             behaviour people expect from a board. */
          collisionDetection={pointerWithin}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={() => {
            setActiveId(null);
            setActiveFrom(null);
          }}
        >
          <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {COLUMN_ORDER.map((s) => (
              <Column
                key={s}
                severity={s}
                cards={visible[s]}
                activeFrom={activeFrom}
                editing={editing}
              />
            ))}
          </div>

          {/* Follows the cursor at full opacity while the original stays
              dimmed in place. */}
          <DragOverlay dropAnimation={null}>
            {activeCard && activeFrom ? (
              <div className="w-[280px] rotate-2 cursor-grabbing">
                <PolicyCardBody card={activeCard} severity={activeFrom} dragging />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </PageShell>
  );
}
